import { useCallback, useRef } from 'react'

/**
 * Motif d'activation en un geste, partagé par tout élément interactif du
 * socle : `pointerdown` déclenche avant que `click` ne soit synthétisé — 50 à
 * 80 ms gagnés, décisif sur les jeux chronométrés (SPEC §5) — `click` reste
 * câblé en filet, pour l'activation clavier (Entrée/Espace) et pour tout
 * appareil qui n'émet pas d'évènements pointeur. Un verrou local empêche le
 * double déclenchement quand les deux évènements se produisent pour le même
 * geste. Extrait de `ReportItemButton`, pensé pour être réutilisé par les
 * seize mécaniques chronométrées (bouton de réponse, case à cocher, etc.).
 *
 * @param onActivate Appelé une seule fois par geste. Reçoit `reset`, à
 *   appeler explicitement pour réarmer le verrou (ex. après l'échec d'un
 *   appel réseau déclenché par l'activation) — sans quoi un second geste sur
 *   le même élément resterait sans effet.
 * @param disabled Empêche toute activation tant que vrai, sans affecter le
 *   verrou (ex. pendant l'envoi en cours, ou une fois l'action déjà faite).
 */
export function usePointerActivation(onActivate: (reset: () => void) => void, disabled = false) {
  const firedRef = useRef(false)

  const reset = useCallback(() => {
    firedRef.current = false
  }, [])

  const trigger = useCallback(() => {
    if (disabled || firedRef.current) return
    firedRef.current = true
    onActivate(reset)
  }, [disabled, onActivate, reset])

  return { onPointerDown: trigger, onClick: trigger, reset }
}
